/**
 * 模型中心 — 展示全部 AI 模型，按类别分类，支持选择启用
 */
import { useState, useEffect } from 'react';
import './ModelCenter.css';

interface Model {
  id: string;
  name: string;
  description: string;
  supplier: string;
  category: 'text' | 'image' | 'video' | 'audio';
  vision?: boolean;
  reasoning?: boolean;
  brand?: string;
}

interface Props {
  selectedModels: string[];
  onSelectedModelsChange: (models: string[]) => void;
  userPoints?: number;
}

const CATEGORIES = ['text', 'image', 'video', 'audio'] as const;

const CATEGORY_META: Record<typeof CATEGORIES[number], { label: string; icon: string; desc: string }> = {
  text:  { label: '文本对话', icon: '💬', desc: '大语言模型，用于对话、推理、代码等' },
  image: { label: '图片生成', icon: '🎨', desc: 'AI 文生图、图生图、图像编辑' },
  video: { label: '视频生成', icon: '🎬', desc: 'AI 文生视频、图生视频、视频处理' },
  audio: { label: '语音合成', icon: '🎙️', desc: '语音合成、声音克隆、音色定制' },
};

const SUPPLIER_ORDER = ['阿里云百炼', '卡比兽API', '速创API', '本地CLI'];

// 定价数据（积分/单位）
const PRICING: Record<string, { label: string; input?: number; output?: number; per?: number; perUnit?: string; isFree?: boolean; note?: string }> = {
  // ── 阿里云百炼 文本 ──
  'qwen3.8-max':            { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6, note: '限时免费10倍' },
  'qwen3.7-max':            { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6 },
  'qwen3.7-plus':           { label: '0.2/1K 输入 · 0.8/1K 输出', input: 0.2, output: 0.8 },
  'qwen3.7-flash':          { label: '0.05/1K 输入 · 0.2/1K 输出', input: 0.05, output: 0.2 },
  'qwen3.6-plus':           { label: '0.2/1K 输入 · 0.8/1K 输出', input: 0.2, output: 0.8 },
  'qwen3.6-flash':          { label: '0.05/1K 输入 · 0.2/1K 输出', input: 0.05, output: 0.2 },
  'deepseek-v4-pro':        { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6 },
  'deepseek-v4-flash-0731': { label: '0.1/1K 输入 · 0.4/1K 输出', input: 0.1, output: 0.4 },
  'deepseek-v3.2':          { label: '0.2/1K 输入 · 0.8/1K 输出', input: 0.2, output: 0.8 },
  'deepseek-r1':            { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6 },
  'kimi-k2.7-code':         { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6 },
  'kimi-k2.6':              { label: '0.2/1K 输入 · 0.8/1K 输出', input: 0.2, output: 0.8 },
  'kimi-k2-thinking':       { label: '0.8/1K 输入 · 3.2/1K 输出', input: 0.8, output: 3.2 },
  'kimi/kimi-k3':           { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6 },
  'glm-5.2':                { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6 },
  'glm-5.1':                { label: '0.2/1K 输入 · 0.8/1K 输出', input: 0.2, output: 0.8 },
  'MiniMax-M2.5':           { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6 },
  'MiniMax/MiniMax-M3':     { label: '0.4/1K 输入 · 1.6/1K 输出', input: 0.4, output: 1.6 },
  'xiaomi/mimo-v2.5-pro':   { label: '0.2/1K 输入 · 0.8/1K 输出', input: 0.2, output: 0.8 },
  // ── 阿里云百炼 图片 ──
  'wanx2.1-t2i-turbo':      { label: '4 积分/张', per: 4, perUnit: '张' },
  'wanx2.1-t2i-plus':       { label: '8 积分/张', per: 8, perUnit: '张' },
  'wanx2.1-imageedit':      { label: '10 积分/张', per: 10, perUnit: '张' },
  // ── 卡比兽API 文本 ──
  'claude-sonnet-4-6':           { label: '1.1/1K 输入 · 5.4/1K 输出', input: 1.1, output: 5.4 },
  'claude-sonnet-4-6-thinking':  { label: '2.2/1K 输入 · 10.8/1K 输出', input: 2.2, output: 10.8 },
  'claude-opus-4-8':             { label: '1.8/1K 输入 · 9/1K 输出', input: 1.8, output: 9 },
  'claude-opus-5':               { label: '27/1K 统一计费', input: 27, output: 27 },
  'gpt-5.4':                     { label: '1.4/1K 输入 · 8.6/1K 输出', input: 1.4, output: 8.6 },
  'gpt-5.4-mini':                { label: '0.4/1K 输入 · 2.6/1K 输出', input: 0.4, output: 2.6 },
  'gpt-5.5':                     { label: '2.9/1K 输入 · 17.3/1K 输出', input: 2.9, output: 17.3 },
  'gemini-2.5-flash':            { label: '0.3/1K 输入 · 2.7/1K 输出', input: 0.3, output: 2.7 },
  'gemini-2.5-pro':              { label: '1.4/1K 输入 · 10.8/1K 输出', input: 1.4, output: 10.8 },
  'gemini-3-flash-preview':      { label: '0.5/1K 输入 · 2.2/1K 输出', input: 0.5, output: 2.2 },
  'grok-4.20-0309':              { label: '1.7/1K 输入 · 5.0/1K 输出', input: 1.7, output: 5.0 },
  'grok-4.3':                    { label: '1.0/1K 输入 · 2.1/1K 输出', input: 1.0, output: 2.1 },
  // ── 本地CLI ──
  'claude-sonnet-4-cli':   { label: '订阅制 · 无限额', isFree: true },
  'claude-opus-4-cli':     { label: '订阅制 · 无限额', isFree: true },
  'gpt-4o-cli':            { label: '订阅制 · 无限额', isFree: true },
  'o3-mini-cli':           { label: '订阅制 · 无限额', isFree: true },
  'gemini-2.5-pro-cli':    { label: '订阅制 · 无限额', isFree: true },
  'gemini-2.5-flash-cli':  { label: '订阅制 · 无限额', isFree: true },
  // ── 速创API 图片 ──
  'NanoBanana2':        { label: '10 积分/张', per: 10, perUnit: '张' },
  'NanoBanana_pro':     { label: '30 积分/张', per: 30, perUnit: '张' },
  'NanoBanana-2-Lite':  { label: '8 积分/张', per: 8, perUnit: '张' },
  'GPT-Image-2':        { label: '10 积分/张', per: 10, perUnit: '张' },
  'Wan2.7-image':       { label: '20 积分/张', per: 20, perUnit: '张' },
  // ── 速创API 视频 ──
  'video_vidu':     { label: '100 积分/秒', per: 100, perUnit: '秒' },
  'video_omni':     { label: '100 积分/秒', per: 100, perUnit: '秒' },
  'MiniMax-H3':     { label: '30 积分/秒',  per: 30, perUnit: '秒' },
  'google_omni':    { label: '10 积分/秒',  per: 10, perUnit: '秒' },
  'veo3.1_fast':    { label: '5 积分/秒',   per: 5, perUnit: '秒' },
  'Wan2.7-video':   { label: '30 积分/秒',  per: 30, perUnit: '秒' },
  'Digital_Humans': { label: '2 积分/秒',   per: 2, perUnit: '秒' },
  'Video_Upscaling':{ label: '10 积分/秒',  per: 10, perUnit: '秒' },
  // ── 速创API 音频 ──
  'wuyin-tts':          { label: '60 积分/千字符', per: 60, perUnit: '千字符' },
  'wuyin-voice-clone':  { label: '600 积分/次',    per: 600, perUnit: '次' },
};

const SUPPLIER_COLOR: Record<string, string> = {
  '阿里云百炼': '#FF6B2C',
  '卡比兽API':  '#007AFF',
  '速创API':    '#30D158',
  '本地CLI':    '#5856D6',
};

export default function ModelCenter({ selectedModels, onSelectedModelsChange, userPoints }: Props) {
  const [models, setModels] = useState<Model[]>([]);
  const [activeCategory, setActiveCategory] = useState<typeof CATEGORIES[number]>('text');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const selected = new Set(selectedModels);

  useEffect(() => {
    fetch('/api/models')
      .then((r) => r.json())
      .then((data) => {
        const list: Model[] = data.models || data.data || [];
        setModels(list.filter((m) => m.category));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedModelsChange([...next]);
  };

  const catModels = models.filter(
    (m) =>
      m.category === activeCategory &&
      (search === '' ||
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.description.toLowerCase().includes(search.toLowerCase())),
  );

  const bySupplier = SUPPLIER_ORDER.reduce<Record<string, Model[]>>((acc, s) => {
    const group = catModels.filter((m) => m.supplier === s);
    if (group.length > 0) acc[s] = group;
    return acc;
  }, {});

  const totalSelected = selectedModels.length;
  const catSelected = catModels.filter((m) => selected.has(m.id)).length;

  return (
    <div className="mc">
      {/* 顶部 header */}
      <div className="mc__header">
        <div className="mc__header-left">
          <h1 className="mc__title">模型中心</h1>
          <span className="mc__subtitle">管理你的 AI 模型偏好，按需启用</span>
        </div>
        <div className="mc__header-right">
          {userPoints !== undefined && (
            <div className="mc__points">
              <span className="mc__points-icon">💰</span>
              <span className="mc__points-val">{userPoints.toLocaleString()}</span>
              <span className="mc__points-label">积分</span>
            </div>
          )}
          <div className="mc__selected-badge">{totalSelected} 个已启用</div>
        </div>
      </div>

      {/* 说明条 */}
      <div className="mc__info-bar">
        <span>积分汇率：1 RMB = 100 积分 · 注册赠 10,000 积分</span>
      </div>

      {/* 分类标签 */}
      <div className="mc__cats">
        {CATEGORIES.map((cat) => {
          const total = models.filter((m) => m.category === cat).length;
          const sel = models.filter((m) => m.category === cat && selected.has(m.id)).length;
          return (
            <button
              key={cat}
              className={`mc__cat-tab ${activeCategory === cat ? 'mc__cat-tab--active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              <span className="mc__cat-icon">{CATEGORY_META[cat].icon}</span>
              <span className="mc__cat-label">{CATEGORY_META[cat].label}</span>
              <span className="mc__cat-badge">{sel}/{total}</span>
            </button>
          );
        })}
      </div>

      {/* 搜索 + 描述 */}
      <div className="mc__toolbar">
        <div className="mc__search-wrap">
          <span className="mc__search-icon">🔍</span>
          <input
            className="mc__search"
            placeholder="搜索模型名称或描述…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="mc__cat-desc">{CATEGORY_META[activeCategory].desc} · 已选 {catSelected}/{catModels.length}</span>
      </div>

      {/* 模型列表 */}
      <div className="mc__body">
        {loading ? (
          <div className="mc__empty">加载中…</div>
        ) : catModels.length === 0 ? (
          <div className="mc__empty">暂无模型</div>
        ) : (
          Object.entries(bySupplier).map(([supplier, mods]) => (
            <div key={supplier} className="mc__supplier-group">
              <div className="mc__supplier-header">
                <span
                  className="mc__supplier-dot"
                  style={{ background: SUPPLIER_COLOR[supplier] || '#888' }}
                />
                <span className="mc__supplier-name">{supplier}</span>
                <span className="mc__supplier-count">{mods.filter((m) => selected.has(m.id)).length}/{mods.length}</span>
                <button
                  className="mc__supplier-toggle"
                  onClick={() => {
                    const ids = mods.map((m) => m.id);
                    const allSelected = ids.every((id) => selected.has(id));
                    const next = new Set(selected);
                    if (allSelected) ids.forEach((id) => next.delete(id));
                    else ids.forEach((id) => next.add(id));
                    onSelectedModelsChange([...next]);
                  }}
                >
                  {mods.every((m) => selected.has(m.id)) ? '全部取消' : '全选'}
                </button>
              </div>
              <div className="mc__model-grid">
                {mods.map((m) => {
                  const isSelected = selected.has(m.id);
                  const p = PRICING[m.id];
                  return (
                    <div
                      key={m.id}
                      className={`mc__model-card ${isSelected ? 'mc__model-card--on' : ''}`}
                      onClick={() => toggle(m.id)}
                    >
                      {/* 左上：勾选状态 */}
                      <div className={`mc__check ${isSelected ? 'mc__check--on' : ''}`}>
                        {isSelected && (
                          <svg width="10" height="10" viewBox="0 0 10 10">
                            <polyline points="1.5,5 4,7.5 8.5,2" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
                          </svg>
                        )}
                      </div>

                      {/* 模型名 */}
                      <div className="mc__model-name">{m.name}</div>

                      {/* 描述 */}
                      <div className="mc__model-desc">{m.description}</div>

                      {/* 定价 */}
                      {p && (
                        <div className={`mc__pricing ${p.isFree ? 'mc__pricing--free' : ''}`}>
                          {p.isFree ? (
                            <span className="mc__price-free">订阅制 · 无限额</span>
                          ) : p.input !== undefined ? (
                            <>
                              <span className="mc__price-row">
                                <span className="mc__price-key">输入</span>
                                <span className="mc__price-val">{p.input} pt/1K</span>
                              </span>
                              <span className="mc__price-row">
                                <span className="mc__price-key">输出</span>
                                <span className="mc__price-val">{p.output} pt/1K</span>
                              </span>
                            </>
                          ) : (
                            <span className="mc__price-row">
                              <span className="mc__price-val">{p.per} 积分/{p.perUnit}</span>
                            </span>
                          )}
                          {p.note && <span className="mc__price-note">{p.note}</span>}
                        </div>
                      )}

                      {/* 能力标签 */}
                      <div className="mc__tags">
                        {m.vision && <span className="mc__tag mc__tag--vision">视觉</span>}
                        {m.reasoning && <span className="mc__tag mc__tag--reason">推理</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
